<template>
  <div class="round-checkbox">
    <div class="round-checkbox__list">
      <canvas
        class="round-checkbox__canvas"
        ref="canvas"
        :style="{ animationDelay: `${(list.length + 1) * 100}ms` }"
      ></canvas>
      <div
        v-for="(item, index) of list"
        :key="index"
        class="round-checkbox__item check"
        :class="[{disable: isDisable(item)}]"
        :style="calcTransformShift(index)"
        @click="itemClick(item)"
      >
        <div
          class="check__circle"
          :class="{ 'check__circle--active': active(item) }"
        ></div>
        <div class="check__label" :style="getTextPositionStyle(index)">
          {{ $t(item) + getAfterValues(index) }}
          <span class="check__label-down" v-html="getHtmlValues(index)"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Round from '@/class/Round';
import CanvasLine from '@/class/CanvasLine';
import { RoundDirection } from '@/models/round-direction';
import { NavigationDirection } from '@/models/navigation-direction';

export default {
  props: {
    value: {
      type: Array,
      default() {
        return [];
      },
    },
    list: {
      type: Array,
      default() {
        return [];
      },
    },
    listAfterValues: {
      type: Array,
      default() {
        return [];
      },
    },
    listHtmlValues: {
      type: Array,
      default() {
        return [];
      },
    },
    activeList: {
      type: Array,
      default() {
        return [];
      },
    },
    multiple: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selected: [],
      coords: [],
      canvasLine: null,
      disabled: [],
    };
  },
  async mounted() {
    this.create();
    this.list.forEach(item => {
      if (!this.activeList.includes(item)) {
        this.disabled.push(item);
      }
    });
  },
  watch: {
    value: {
      handler: 'setSelected',
      immediate: true,
    },
    selected: {
      handler: 'setSelectedCoords',
      immediate: false,
    },
    circleSize: {
      handler: 'create',
      immediate: false,
    },
  },
  computed: {
    circleSize() {
      return this.$store.getters.circleSize;
    },
  },
  methods: {
    getAfterValues(index) {
      if (!this.listAfterValues.length || !this.listAfterValues[index]) {
        return '';
      } else {
        return ` - ${this.listAfterValues[index]}`;
      }
    },
    getHtmlValues(index) {
      if (!this.listHtmlValues.length || !this.listHtmlValues[index]) {
        return '';
      } else {
        return this.listHtmlValues[index];
      }
    },
    isDisable(item) {
      return this.disabled.includes(item);
      // return !this.activeList.includes(item);
    },
    async create() {
      this.canvasLine = null;
      this.coords = [];
      const shift = new Round(this.circleSize * 0.4, this.list.length);
      for (let index in this.list) {
        await this.coords.push(shift.getShift(index));
      }
      this.$refs.canvas.width = this.circleSize * 0.808;
      this.$refs.canvas.height = this.circleSize * 0.808;
      this.canvasLine = new CanvasLine({
        canvas: this.$refs.canvas,
        list: this.list,
        coords: this.coords,
        selected: this.selected,
      });
    },
    setSelected() {
      if (this.value) this.selected = this.value;
    },
    setSelectedCoords() {
      if (!this.canvasLine) {
        this.setSelectedCoords(this.selected);
      } else {
        this.canvasLine.setCoordinates(this.selected);
      }
    },
    add(value) {
      if (!this.multiple) {
        this.selected = [value];
      } else {
        this.selected.push(value);
      }
    },
    remove(value) {
      const idx = this.selected.indexOf(value);
      this.selected.splice(idx, 1);
    },
    itemClick(value) {
      if (this.isDisable(value)) return;
      this.setSelectedCoords();
      if (this.selected.includes(value)) {
        this.remove(value);
      } else {
        this.add(value);
      }
      this.$emit('input', this.selected);
    },
    active(value) {
      return this.selected.includes(value);
    },

    getPositionIndex(index) {
      let posN = -((this.list.length - 1) / 2 - index);
      if (this.list.length % 2) {
        posN = Math.round(posN);
      }
      return posN;
    },
    getShiftCoef(index) {
      const posN = this.getPositionIndex(index);
      const length = this.list.length;
      let lenthCoeficient = length > 8 ? 4 : 1.5;
      let shiftCoef;

      if (index === 0 || index === length - 1) {
        shiftCoef = 0;
      } else {
        shiftCoef = (length - Math.abs(posN)) * (length / lenthCoeficient);
      }

      return Math.round(shiftCoef);
    },
    getTextPositionStyle(index) {
      const cSize = 16;
      const length = this.list.length;
      const posN = this.getPositionIndex(index);
      const shiftCoef = this.getShiftCoef(index);
      let style = `transform: translate(-50%, ${cSize * 2}px)`;
      if (index === 0 && length !== 1) {
        style = `transform: translate(calc(-100% - ${cSize}px), ${cSize}px)`;
      } else if (index === length - 1 && length !== 1) {
        style = `transform: translate(${cSize}px, ${cSize}px)`;
      } else if (posN > 0.5) {
        style = `transform: translate(${Math.max(
          length / 4 - shiftCoef,
          cSize / 3
        )}px, ${shiftCoef * 1.5}px)`;
      } else if (posN < -0.5) {
        style = `transform: translate(calc(-100% - ${Math.max(
          length / 4 - shiftCoef,
          cSize / 3
        )}px), ${shiftCoef * 1.5}px)`;
      }
      return style;
    },

    calcTransformShift(index) {
      const c = this.coords[index];
      if (!c) return;
      if (this.list.length === 1) {
        return {
          transform: `translate(0px, -${this.circleSize * 0.4}px)`,
        };
      }
      return {
        transform: `translate(${c.x}px, ${c.y}px)`,
      };
    },
  },
};
</script>

<style lang="scss">
.round-checkbox {
  position: absolute;
  top: calc(10% - 4px);
  width: calc(80% + 4px);
  height: calc(80% + 4px);
  border-radius: 50%;
}

.round-checkbox__list {
  position: relative;
  height: 100%;
}

.check {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 1;

  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  transform: translate(0, 0);
  cursor: pointer;

  &:focus {
    outline: none;
  }
}

$maxDelay: 9 * 100;
@for $i from 1 through 20 {
  $delay: floor($maxDelay / $i);
  .check:nth-child(#{$i}) {
    opacity: 0;
    transition: all
      550ms
      #{$delay}ms
      cubic-bezier(0.35, 0.07, 0.4, #{$i * 0.025 + 1});

    animation: CheckFadeIn 250ms #{floor($delay + 300)}ms forwards;
  }
}

@keyframes CheckFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

$cSize: 16px;
.check__circle {
  width: $cSize;
  height: $cSize;
  transform: translate(-50%, -50%);
  border: 2px solid white;
  border-radius: 50%;
  background-color: $brownMedium;

  transition: 150ms ease-in;

  &:focus {
    outline: none;
  }
}

.check__circle--active {
  width: $cSize * 1.5;
  height: $cSize * 1.5;

  background-color: $green;
}

.check__label {
  position: absolute;
  white-space: nowrap;
  color: white;
  font-size: 1.8em;
  line-height: $cSize;
  top: -$cSize / 2;
  left: 0;
  font-weight: bold;
  text-align: center;
}

.check__label-down {
  display: block;
  font-weight: 300;
  margin-top: 5px;
}

.check.disable {
  cursor: default;
  
  .check__circle {
    border-color: rgba(white, 0.4);
  }
  .check__label {
    color: rgba(white, 0.4);
  }
}

.round-checkbox__canvas {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;

  opacity: 0;
  animation: CanvasFadeIn 600ms 300ms forwards;
}

@keyframes CanvasFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 0.6;
  }
}

@media (max-width: 460px) {
  .check__label {
    font-weight: normal;
    letter-spacing: 1px;
  }
}

@media (min-width: 460.89px) {
  .check__circle {
    border-width: 4px;
  }
}

@media (min-width: 992.89px) {
  .check__circle--active {
    width: $cSize * 2;
    height: $cSize * 2;
  }
}
</style>
